import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

import {
  isOnMobileApp,
  requestAuthTokensFromMobile,
} from '../composables/useMobileLogin';
import { useRootStore } from '../stores/root';

export function isAlreadyLoggedIn() {
  const cognitolsKeys = Object.keys(localStorage).filter((key) =>
    key.startsWith('CognitoIdentityServiceProvider'),
  );

  if (cognitolsKeys.length < 4) {
    // console.log('Not enough keys in local storage', cognitolsKeys);
    return false;
  }

  const accessToken = cognitolsKeys.find((key) => key.endsWith('accessToken'));
  const idToken = cognitolsKeys.find((key) => key.endsWith('idToken'));
  const refreshToken = cognitolsKeys.find((key) =>
    key.endsWith('refreshToken'),
  );
  const userData = cognitolsKeys.find((key) => key.endsWith('userData'));

  if (!accessToken || !idToken || !refreshToken || !userData) {
    // console.log('Not all tokens are present in local storage', {
    //   accessToken,
    //   idToken,
    //   refreshToken,
    //   userData,
    // });
    return false;
  }

  // console.log('All tokens are present in local storage');
  return true;
}

// We don't want to wait more than 2 seconds for the mobile app to respond
// We'll use a timeout and a trigger to wait for the token and move on or
// timeout if it doesn't come. This function SHOULD be called almost instantly
// So, 2 seconds should be enough time to wait. If it takes longer than 2 seconds,
// that means the mobile app likely errored out and we're not going to wait
export async function getMobileAuth() {
  let toRef: number;
  let finishTrigger: () => void;

  // Timeout trigger. Waits 1 second, then throws. We're setting this up to
  // be a closure, so we move the resolve function to the parent so that the
  // request portion can resolve it prior to the timeout
  const timeout = () =>
    new Promise<void>((resolve, reject) => {
      toRef = window.setTimeout(() => {
        reject(new Error('Timeout waiting for mobile auth'));
      }, 1000);

      finishTrigger = () => {
        window.clearTimeout(toRef);
        resolve();
      };
    });

  // console.log('Requesting mobile auth');

  // const start = performance.now();

  // This requests the tokens. When the token receipt function finishes, we run
  // the finish trigger to clear the timeout.
  const request = async () => {
    await requestAuthTokensFromMobile();
    finishTrigger?.();
  };

  await Promise.all([timeout(), request()]);

  // const end = performance.now();

  // console.log(`Total time to get mobile auth: ${end - start}ms`);
}

export async function initMobileAuth() {
  // Ask for mobile credentials
  if (!isAlreadyLoggedIn()) {
    try {
      await getMobileAuth();
    } catch (_e) {
      // console.log('mobile request timed out');
      // We'll move on if we can't get the tokens
    }
  } else {
    // console.log('Already logged in');
  }
}

export async function mobileAuthNavGuard(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  // Only does this if we're on the mobile app. Hopefully is close to a no-op
  // on the web app
  if (isOnMobileApp()) {
    const { user } = useRootStore();

    // console.log('mobileAuthNavGuard', {
    //   user,
    //   isAlreadyLoggedIn: isAlreadyLoggedIn(),
    // });

    if (!user || !isAlreadyLoggedIn()) {
      await initMobileAuth();
    }
  }

  next();
}
